/**
 * Sidebar.tsx — primary navigation: sticky themed rail at `md`+ (collapsible to
 * icons), off-canvas drawer below `md`. Responsiveness is CSS only; the drawer
 * and collapse state live in AppShell. Disabled stubs keep the data-driven
 * live/stub split from `navItems.ts` (WCAG 4.1.2: native `disabled`).
 */

import { useId, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { getThemePref, setThemePref, type ThemePref } from '../../theme';
import { NAV_ITEMS, type NavItem } from './navItems';

export interface SidebarProps {
  activeId?: string;
  /** Icon-rail mode (only applies at `md`+; the drawer is always full width). */
  collapsed?: boolean;
  /** Drawer open state (only applies below `md`). */
  open?: boolean;
  id?: string;
}

// Inline 24x24 stroke icons (no icon library installed). One path set per nav id.
const ICONS: Record<NavItem['id'], string> = {
  health: 'M3 12h4l3-8 4 16 3-8h4', // pulse
  leads:
    'M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2M9 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8M22 21v-2a4 4 0 0 0-3-3.9', // people
  conversations: 'M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z', // chat bubble
  config: 'M4 6h10M18 6h2M4 12h4M12 12h8M4 18h12M20 18h0M14 4v4M8 10v4M16 16v4', // sliders
  numbers: 'M5 9h14M5 15h14M10 3 8 21M16 3l-2 18', // hash
  products: 'M21 8 12 3 3 8v8l9 5 9-5zM3 8l9 5 9-5M12 13v8', // box
};

const NavIcon = ({ id }: { id: NavItem['id'] }) => (
  <svg
    aria-hidden="true"
    data-testid={`nav-icon-${id}`}
    viewBox="0 0 24 24"
    className="h-6 w-6 shrink-0"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d={ICONS[id]} />
  </svg>
);

const THEMES: readonly ThemePref[] = ['system', 'dark', 'light'];

const ThemeSwitch = () => {
  const { t } = useTranslation();
  const [pref, setPref] = useState<ThemePref>(getThemePref);
  const uid = useId();
  return (
    <fieldset className="flex flex-col gap-1 px-1">
      <legend className="px-1 pb-1 text-meta text-ink-3">{t('shell.theme')}</legend>
      <div className="flex gap-1 rounded-lg bg-surface-2 p-1">
        {THEMES.map((value) => (
          <label key={value} htmlFor={`${uid}-${value}`} className="relative flex-1">
            <input
              id={`${uid}-${value}`}
              type="radio"
              name="tcn-theme"
              value={value}
              checked={pref === value}
              onChange={() => {
                setThemePref(value);
                setPref(value);
              }}
              data-testid={`theme-${value}`}
              className="peer sr-only"
            />
            <span className="flex min-h-[32px] cursor-pointer items-center justify-center rounded-md px-2 text-meta text-ink-2 hover:text-ink-1 peer-checked:bg-surface-3 peer-checked:font-semibold peer-checked:text-ink-1 peer-focus-visible:outline peer-focus-visible:outline-2 peer-focus-visible:outline-outline-focus">
              {t(`shell.${value}`)}
            </span>
          </label>
        ))}
      </div>
    </fieldset>
  );
};

export const Sidebar = ({
  activeId = 'health',
  collapsed = false,
  open = false,
  id = undefined,
}: SidebarProps) => {
  const { t } = useTranslation();
  // Labels collapse only at md+ (CSS); the drawer below md always shows them.
  const hideLabel = collapsed ? 'md:hidden' : '';
  return (
    <nav
      id={id}
      aria-label={t('nav.ariaLabel')}
      data-testid="sidebar"
      data-collapsed={collapsed ? 'true' : 'false'}
      className={`fixed inset-y-0 left-0 z-40 flex w-60 flex-col gap-1 overflow-y-auto border-r border-outline bg-surface-nav p-3 shadow-xl transition-transform md:sticky md:top-0 md:h-screen md:shrink-0 md:translate-x-0 md:self-start md:shadow-none md:visible ${
        collapsed ? 'md:w-16 md:px-2' : 'md:w-60'
      } ${open ? 'translate-x-0' : '-translate-x-full invisible'}`}
    >
      <div className="px-2 pb-4 pt-2">
        <p className="text-h2 font-bold uppercase tracking-wide text-ink-1">
          <span className="sr-only">{t('app.title')}</span>
          <span aria-hidden="true">
            TCN<span className="text-selected-dot"> | </span>
            <span className={hideLabel}>CAPITAL</span>
          </span>
        </p>
        <p className={`text-meta text-ink-3 ${hideLabel}`}>{t('app.subtitle')}</p>
      </div>
      <ul className="flex flex-col gap-1">
        {NAV_ITEMS.map((item) => {
          const label = t(`nav.${item.id}`);
          const glyph = <NavIcon id={item.id} />;
          if (!item.enabled) {
            return (
              <li key={item.id}>
                <button
                  type="button"
                  disabled
                  aria-disabled="true"
                  data-testid={`nav-${item.id}`}
                  data-enabled="false"
                  aria-label={t('nav.comingSoonAria', { label })}
                  title={label}
                  className="flex min-h-[44px] w-full cursor-not-allowed items-center gap-2 rounded-md px-3 py-2 text-left text-body text-ink-3"
                >
                  {glyph}
                  <span className={hideLabel}>{label}</span>
                  <span className={`rounded bg-surface-3 px-1.5 py-0.5 text-meta ${hideLabel}`}>
                    {t('nav.comingSoon')}
                  </span>
                </button>
              </li>
            );
          }
          const isActive = item.id === activeId;
          return (
            <li key={item.id}>
              <a
                href={item.href ?? `#${item.id}`}
                data-testid={`nav-${item.id}`}
                data-enabled="true"
                aria-current={isActive ? 'page' : undefined}
                aria-label={label}
                title={label}
                className={`flex min-h-[44px] w-full items-center gap-2 rounded-md border-l-[3px] px-3 py-2 text-body ${
                  isActive
                    ? 'border-gold bg-surface-2 font-semibold text-gold'
                    : 'border-transparent text-ink-2 hover:bg-surface-2 hover:text-ink-1'
                }`}
              >
                {glyph}
                <span className={hideLabel}>{label}</span>
              </a>
            </li>
          );
        })}
      </ul>
      <div className={`mt-auto pt-4 ${hideLabel}`}>
        <ThemeSwitch />
      </div>
    </nav>
  );
};
